import { artVon, nettoVon, type Posten, type Rechenbasis } from "./rechnen";
import { EINNAHME, FAHRZEUG, GWG, PAUSCHALEN, VERRECHNET, cent } from "./typen";

export type Pruefpunkt = {
  punkt: string;
  posten: Posten | null;
  hinweis: string;
};

export const PUNKT = {
  duplikat: "Duplikat",
  ohneKategorie: "Ohne Kategorie",
  unsicher: "Unsicherer Fall",
  grenze: "Über 250 €: Rechnung auf die Firma prüfen",
  gwg: "Über GWG-Grenze: Abschreibung prüfen",
  beleg: "Fehlender Beleg",
  p13b: "§ 13b: Steuerschuldumkehr prüfen (Kz 46/47/67)",
  ige: "Innergemeinschaftlicher Erwerb prüfen (Kz 89/61)",
  nullSatz: "Einnahme ohne USt: Kennzahl prüfen",
  doppelt: "Mögliche Doppelzählung mit Rechnung",
  stripe: "Stripe-Abstimmung",
  fahrzeug: "Fahrzeugkosten und km-Pauschale zugleich",
};

export const pruefen = (posten: Posten[], basis: Rechenbasis): Pruefpunkt[] => {
  const { jahr } = basis;
  const liste: Pruefpunkt[] = [];
  const dazu = (punkt: string, p: Posten | null, hinweis = "") =>
    liste.push({ punkt, posten: p, hinweis });
  const rechnungsBetraege = new Map(
    basis.rechnungen.map((r) => [
      cent(r.brutto_19) + cent(r.brutto_7) + cent(r.brutto_0),
      r.nummer,
    ]),
  );

  for (const p of posten) {
    const art = artVon(p.kategorie);
    const hinweis = p.unsicher.join(" / ");
    if (p.duplikat)
      dazu(
        PUNKT.duplikat,
        p,
        "Bei doppeltem Export eine Zeile auf Ignorieren setzen",
      );
    if (!p.kategorie) dazu(PUNKT.ohneKategorie, p, hinweis);
    else if (p.unsicher.length) dazu(PUNKT.unsicher, p, hinweis);
    if (art === "Ausgabe" && p.brutto < -cent(jahr.grenze_rechnung))
      dazu(PUNKT.grenze, p);
    if (
      p.kategorie === GWG &&
      nettoVon(p.brutto, p.satz) < -cent(jahr.grenze_gwg)
    )
      dazu(PUNKT.gwg, p);
    if (art === "Ausgabe" && !p.beleg) dazu(PUNKT.beleg, p);
    if (p.sonderfall === "13b")
      dazu(
        PUNKT.p13b,
        p,
        "EU-Leistende: Kz 46/47, sonst Kz 84/85, Vorsteuer Kz 67",
      );
    if (p.sonderfall === "ige")
      dazu(PUNKT.ige, p, "19 %: Kz 89, 7 %: Kz 93, Vorsteuer Kz 61");
    if (art === "Einnahme" && p.satz === 0 && jahr.kur_verzicht)
      dazu(PUNKT.nullSatz, p);
    if (
      p.kategorie === EINNAHME &&
      p.buchungId &&
      rechnungsBetraege.has(p.brutto)
    ) {
      dazu(
        PUNKT.doppelt,
        p,
        `Betrag passt zu Rechnung ${rechnungsBetraege.get(p.brutto)}: Zeile auf „${VERRECHNET}“ setzen`,
      );
    }
  }

  const auszahlungen = basis.buchungen
    .filter(
      (b) =>
        b.datum.startsWith(`${jahr.jahr}-`) &&
        /stripe/i.test(`${b.partner} ${b.text}`) &&
        b.betrag > 0,
    )
    .reduce((s, b) => s + cent(b.betrag), 0);
  const umsaetze = basis.stripe
    .filter((s) => s.datum.startsWith(`${jahr.jahr}-`))
    .reduce((s, u) => s + cent(u.brutto) - cent(u.gebuehr), 0);
  if (auszahlungen !== umsaetze) {
    dazu(
      PUNKT.stripe,
      null,
      `Auszahlungen ${(auszahlungen / 100).toFixed(2)} €, erfasst abzüglich Gebühren ${(umsaetze / 100).toFixed(2)} €`,
    );
  }

  if (
    posten.some(
      (p) => p.kategorie === PAUSCHALEN && p.text === "Geschäftsfahrten",
    ) &&
    posten.some((p) => p.kategorie === FAHRZEUG)
  ) {
    dazu(
      PUNKT.fahrzeug,
      null,
      "Beides für dasselbe Fahrzeug wäre doppelt abgesetzt",
    );
  }
  return liste;
};
